import { RefObject, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { centerScrollTop, findCueTrack, isInsideCue, parseLinkTarget } from "./linkTarget";

/** Колонки встают после метаданных и реплик: ждём, как прежде у `?st=`, — до 40 раз по 250 мс. */
export const LINK_POLL_MS = 250;
export const LINK_MAX_TRIES = 40;
/** Реплики пришли, а строки на экране ещё нет — столько попыток ждём её, потом считаем по времени. */
const ROW_WAIT_TRIES = 4;

/**
 * Открытие папки на реплике по ссылке (`?cue=` из плеера эфира, «Исправить в редакторе ↗»): лента едет к
 * реплике и ставит её посередине колонки, реплика подсвечена лаймовой рамкой, пока не кликнут в другое место.
 * Реплики с таким именем в папке нет — остаётся прежний путь по `st`: его ведёт провайдер (Layout → setTime →
 * setScrollTo(st / zoom)), здесь он не повторяется. Один раз на адрес и папку: дальше ленту ведёт человек.
 *
 * Возвращает id строки (`data-cue-id`), которую подсвечивать, или null.
 */
export const useLinkTarget = ({
    container,
    zoomLevel,
}: {
    container: RefObject<HTMLElement | null>;
    zoomLevel: number;
}): string | null => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const location = useLocation();
    const [highlight, setHighlight] = useState<string | null>(null);
    const doneKey = useRef<string | null>(null);
    const latest = useRef({ state, handles, zoomLevel });
    latest.current = { state, handles, zoomLevel };

    const target = parseLinkTarget(location.search);
    const wanted = Boolean(target.cue);
    const key = `${location.search}|${state.mediaId}|${state.folder}|${state.lang}`;
    const sourceRows = state.translationTrackAll?.[state.originalLang];
    const ready = Boolean(wanted && sourceRows?.length && state.durationFull && zoomLevel > 0);

    useEffect(() => {
        if (!ready || doneKey.current === key) return undefined;
        const { cue } = parseLinkTarget(location.search);
        let tries = 0;
        const timer = window.setInterval(() => {
            tries += 1;
            const { state: s, handles: h, zoomLevel: zoom } = latest.current;
            const box = container.current;
            const giveUp = tries >= LINK_MAX_TRIES;
            if (!box || zoom <= 0) {
                if (giveUp) window.clearInterval(timer);
                return;
            }
            const finish = (top: number | null, row: string | null) => {
                window.clearInterval(timer);
                doneKey.current = key;
                if (top !== null) h.setScrollTo(top);
                if (row) setHighlight(row);
            };
            if (cue) {
                const track = findCueTrack(
                    [s.translationTrackAll?.[s.originalLang], s.translationTrackAll?.[s.lang]],
                    cue,
                    Number(s.folder),
                );
                if (track) {
                    const row = box.querySelector<HTMLElement>(`[data-cue-id="${CSS_ESCAPE(track.id)}"]`);
                    if (row) {
                        const rowTop =
                            row.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
                        finish(centerScrollTop(rowTop, row.offsetHeight, box.clientHeight), track.id);
                        return;
                    }
                    if (tries < ROW_WAIT_TRIES && !giveUp) return;
                    finish(
                        centerScrollTop(track.start / zoom, track.length / zoom, box.clientHeight),
                        track.id,
                    );
                    return;
                }
                if (tries < ROW_WAIT_TRIES && !giveUp) return;
            }
            finish(null, null); // такой реплики нет — лента там, куда её поставил `st`
        }, LINK_POLL_MS);
        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, key]);

    // Новая папка или адрес — прежняя подсветка не про неё.
    useEffect(() => setHighlight(null), [state.mediaId, state.folder, state.lang]);

    useEffect(() => {
        if (!highlight) return undefined;
        const onDown = (event: MouseEvent) => {
            if (!isInsideCue(event.target, highlight)) setHighlight(null);
        };
        document.addEventListener("mousedown", onDown, true);
        return () => document.removeEventListener("mousedown", onDown, true);
    }, [highlight]);

    return highlight;
};

const CSS_ESCAPE = (value: string): string => value.replace(/["\\]/g, "\\$&");
